import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, CheckCheck, X, Clock, AlertTriangle, Calendar, FileText, UserCheck } from 'lucide-react';
import { Button } from './button';
import { Badge } from './badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPO_CONFIG = {
  // Inconsistências
  falta: { icon: X, color: 'text-red-500', bg: 'bg-red-50' },
  atraso: { icon: Clock, color: 'text-orange-500', bg: 'bg-orange-50' },
  marcacao_incompleta: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50' },
  hora_extra_nao_autorizada: { icon: Clock, color: 'text-blue-500', bg: 'bg-blue-50' },
  interjornada_insuficiente: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
  intrajornada_insuficiente: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-50' },
  jornada_excedida: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
  dsr_prejudicado: { icon: Calendar, color: 'text-purple-500', bg: 'bg-purple-50' },
  // Justificativas
  justificativa_pendente: { icon: FileText, color: 'text-amber-500', bg: 'bg-amber-50' },
  justificativa_aprovada: { icon: Check, color: 'text-green-500', bg: 'bg-green-50' },
  justificativa_rejeitada: { icon: X, color: 'text-red-500', bg: 'bg-red-50' },
  // Período
  periodo_proximo_fechamento: { icon: Calendar, color: 'text-amber-500', bg: 'bg-amber-50' },
  periodo_fechado: { icon: CheckCheck, color: 'text-green-600', bg: 'bg-green-50' },
  periodo_reaberto: { icon: Calendar, color: 'text-blue-500', bg: 'bg-blue-50' },
  // Geral
  alerta_geral: { icon: Bell, color: 'text-slate-500', bg: 'bg-slate-50' }
};

const PRIORIDADE_BADGE = {
  urgente: 'bg-red-500 text-white',
  alta: 'bg-orange-500 text-white',
  normal: 'bg-slate-200 text-slate-700',
  baixa: 'bg-slate-100 text-slate-500'
};

const NotificacoesDropdown = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [notificacoes, setNotificacoes] = useState([]);
  const [naoLidas, setNaoLidas] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchContador();
    const interval = setInterval(fetchContador, 60000); // Atualiza a cada minuto
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    if (open) {
      fetchNotificacoes();
    }
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchContador = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/notificacoes/contador`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNaoLidas(data.nao_lidas || 0);
      }
    } catch (err) {
      console.error('Erro ao buscar contador:', err);
    }
  };

  const fetchNotificacoes = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/notificacoes?limite=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotificacoes(data.notificacoes || []);
        setNaoLidas(data.total_nao_lidas || 0);
      }
    } catch (err) {
      console.error('Erro ao buscar notificações:', err);
    } finally {
      setLoading(false);
    }
  };

  const marcarComoLida = async (notificacaoId) => {
    try {
      const res = await fetch(`${API_URL}/api/notificacoes/${notificacaoId}/marcar-lida`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setNotificacoes(prev => prev.map(n => 
          n.id === notificacaoId ? { ...n, lida: true } : n
        ));
        setNaoLidas(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Erro ao marcar como lida:', err);
    }
  };

  const marcarTodasComoLidas = async () => {
    try {
      const res = await fetch(`${API_URL}/api/notificacoes/marcar-todas-lidas`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setNotificacoes(prev => prev.map(n => ({ ...n, lida: true })));
        setNaoLidas(0);
        toast.success('Todas as notificações marcadas como lidas');
      }
    } catch (err) {
      console.error('Erro ao marcar todas como lidas:', err);
    }
  };

  const formatarTempo = (dataStr) => {
    if (!dataStr) return '';
    const data = new Date(dataStr);
    const agora = new Date();
    const diff = agora - data;
    const minutos = Math.floor(diff / 60000);
    const horas = Math.floor(diff / 3600000);
    const dias = Math.floor(diff / 86400000);

    if (minutos < 1) return 'Agora';
    if (minutos < 60) return `${minutos}min atrás`;
    if (horas < 24) return `${horas}h atrás`;
    if (dias < 7) return `${dias}d atrás`;
    return data.toLocaleDateString('pt-BR');
  };

  const NotificacaoItem = ({ notificacao }) => {
    const config = TIPO_CONFIG[notificacao.tipo] || TIPO_CONFIG.alerta_geral;
    const Icon = config.icon;
    const prioridadeBadge = PRIORIDADE_BADGE[notificacao.prioridade] || PRIORIDADE_BADGE.normal;

    return (
      <div 
        className={`p-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${
          notificacao.lida ? 'opacity-60' : ''
        }`}
        onClick={() => !notificacao.lida && marcarComoLida(notificacao.id)}
      >
        <div className="flex gap-3">
          <div className={`p-2 rounded-full ${config.bg} shrink-0`}>
            <Icon className={`w-4 h-4 ${config.color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className={`text-sm font-medium text-slate-800 truncate ${notificacao.lida ? '' : 'font-semibold'}`}>
                {notificacao.titulo}
              </p>
              {!notificacao.lida && (
                <span className="w-2 h-2 bg-blue-500 rounded-full shrink-0"></span>
              )}
            </div>
            <p className="text-xs text-slate-500 line-clamp-2">{notificacao.mensagem}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-slate-400">{formatarTempo(notificacao.created_at)}</span>
              {notificacao.prioridade !== 'normal' && (
                <Badge className={`text-[10px] px-1.5 py-0 ${prioridadeBadge}`}>
                  {notificacao.prioridade}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="sm"
        className="relative p-2"
        onClick={() => setOpen(!open)}
        data-testid="notificacoes-btn"
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {naoLidas > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {naoLidas > 9 ? '9+' : naoLidas}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-800">Notificações</h3>
              <p className="text-xs text-slate-500">{naoLidas} não lida(s)</p>
            </div>
            {naoLidas > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-blue-600 hover:text-blue-700"
                onClick={marcarTodasComoLidas}
              >
                <CheckCheck className="w-4 h-4 mr-1" />
                Marcar todas
              </Button>
            )}
          </div>

          {/* Lista */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-slate-400">
                <div className="animate-spin w-6 h-6 border-2 border-slate-300 border-t-blue-500 rounded-full mx-auto mb-2"></div>
                Carregando...
              </div>
            ) : notificacoes.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p>Nenhuma notificação</p>
              </div>
            ) : (
              notificacoes.map(notificacao => (
                <NotificacaoItem key={notificacao.id} notificacao={notificacao} />
              ))
            )}
          </div>

          {/* Footer */}
          {notificacoes.length > 0 && (
            <div className="p-2 border-t border-slate-100 bg-slate-50">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs text-slate-600"
                onClick={() => {
                  setOpen(false);
                  // Navegar para página de notificações se existir
                }}
              >
                Ver todas as notificações
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificacoesDropdown;
